import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MARQUEE_DURS, MARQUEE_ROWS, track } from "../data/products";
import { useLang } from "../i18n";
import { FadeIn, Micro } from "./Reveal";

function Row({ items, reverse, dur, rowIdx }) {
  const navigate = useNavigate();
  const set = [...items, ...items, ...items, ...items];

  const half = (key) => (
    <div className="flex shrink-0" key={key}>
      {set.map((p, i) => (
        <button
          key={`${p.slug}-${i}`}
          data-testid={`marquee-item-${p.slug}-${rowIdx}-${i}`}
          data-cursor="view"
          onClick={() => {
            track(`product:${p.slug}`);
            navigate(`/catalogue#${p.slug}`);
          }}
          className="group relative flex items-center gap-4 md:gap-5 shrink-0 w-[280px] md:w-[360px] mr-3 md:mr-4 border border-white/10 bg-coal px-4 md:px-5 py-3.5 md:py-5 text-left hover:border-gold/60 transition-colors duration-500"
        >
          <div className="w-12 h-12 md:w-16 md:h-16 overflow-hidden shrink-0">
            <img
              src={p.img}
              alt={p.name}
              loading="lazy"
              onError={(e) => (e.currentTarget.style.opacity = "0")}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition duration-700"
            />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] tracking-[0.3em] text-gold/80 font-semibold">{p.num}</div>
            <div className="font-display uppercase text-sm md:text-base tracking-wide text-cream/85 group-hover:text-cream truncate transition-colors">
              {p.name}
            </div>
          </div>
          <ArrowUpRight className="ml-auto w-4 h-4 text-cream/25 group-hover:text-gold transition-colors shrink-0" />
          <span className="absolute bottom-0 left-0 h-px w-full bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
        </button>
      ))}
    </div>
  );

  return (
    <div className="marquee-row overflow-hidden" data-testid={`marquee-row-${rowIdx}`}>
      <div className={`marquee-track ${reverse ? "marquee-reverse" : ""}`} style={{ "--dur": dur }}>
        {half("a")}
        {half("b")}
      </div>
    </div>
  );
}

export default function ProductMarquee() {
  const { t } = useLang();
  return (
    <section
      id="products"
      data-testid="marquee-section"
      className="scroll-mt-24 relative py-20 md:py-28 border-t border-white/5 overflow-hidden"
    >
      <FadeIn className="px-5 md:px-12 mb-10 flex items-center justify-between">
        <Micro className="text-gold/80">{t("marquee.chapter")}</Micro>
        <Micro className="hidden md:block">{t("marquee.note")}</Micro>
      </FadeIn>

      <div className="relative">
        <div className="space-y-3 md:space-y-4">
          {MARQUEE_ROWS.map((items, i) => (
            <Row key={i} items={items} reverse={i % 2 === 1} dur={MARQUEE_DURS[i]} rowIdx={i} />
          ))}
        </div>

        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-40 bg-gradient-to-r from-ink to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-40 bg-gradient-to-l from-ink to-transparent z-10" />

        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <div className="text-center px-8 py-5 md:py-7 bg-ink/70 backdrop-blur-[6px] border border-gold/20">
            <img
              src="/logo.png"
              alt="INT–EXT"
              className="h-14 md:h-20 mx-auto mix-blend-screen"
            />
            <Micro className="block mt-3 text-gold/80">{t("marquee.brand")}</Micro>
          </div>
        </div>
      </div>
    </section>
  );
}
